import { Box } from "@mui/material";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { currencyFormatter } from "../../constants";
import { Scale_Entity } from "../../entities/scale";
import { toScaledInteger } from "../../helpers/numberHelpers";
import {
  formOpenSelector,
  selectedScaleID,
  setFormOpen,
  setSelectedScaleID,
} from "../../redux/filmsReducer";
import {
  useCreateScaleMutation,
  useDeleteScaleMutation,
  useUpdateScaleMutation,
} from "../../redux/scale/scaleEndpoints";
import { useScale } from "../../redux/scale/scaleHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";
import { useProjectTypes } from "../../redux/projectType/projectTypeHooks";
import { useTaskTypes } from "../../redux/taskType/taskTypeHooks";

/**
 *
 * @returns {ReactElement} FormScale page
 */
export function FormScale() {
  const scaleID = useSelector(selectedScaleID);
  const { scale, isLoading } = useScale(scaleID);
  const open = useSelector(formOpenSelector("scale"));
  const [_scale, setScale] = useState<Partial<Scale_Entity>>({});

  const [createScale, { isLoading: isCreating }] = useCreateScaleMutation();
  const [updateScale, { isLoading: isUpdating }] = useUpdateScaleMutation();
  const [deleteScale] = useDeleteScaleMutation();
  const askQuestion = useAskQuestion();
  const [rateFocused, setRateFocused] = useState(false);
  const [rateValue, setRateValue] = useState("");

  const { projectTypes } = useProjectTypes();
  const { taskTypes } = useTaskTypes();

  const dispatch = useDispatch();

  useEffect(() => {
    if (scale?.id) {
      setScale(scale);
      setRateValue(((scale.rate ?? 0) / 10000).toString());
    }
  }, [scale]);

  const onClose = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "scale" }));
    dispatch(setSelectedScaleID(undefined));
  };

  const save = () => {
    if (scaleID && _scale) {
      updateScale({
        id: scaleID,
        body: {
          ..._scale,
          rate: toScaledInteger(rateValue, 10000),
        },
      });
    } else if (_scale) {
      createScale({
        ..._scale,
        rate: toScaledInteger(rateValue, 10000),
      })
        .unwrap()
        .then((e) => {
          dispatch(setSelectedScaleID(e.id));
        });
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a Project Type. Please confirm this action.
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (scaleID) deleteScale(scaleID);
    onClose();
  };

  const selectedProjectType =
    projectTypes?.find((pt) => pt.id === _scale?.projectTypeID) ?? null;

  const selectedTaskType =
    taskTypes?.find((tt) => tt.id === _scale?.taskTypeID) ?? null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout="center" sx={{ minWidth: 520, maxWidth: "95vw" }}>
        <ModalClose onClick={onClose} />
        <Typography
          startDecorator={<i className="fa-solid fa-money-bill" />}
          level="h6"
          sx={{ fontWeight: "bold" }}
        >
          {scaleID ? "Edit" : "Create"} Scale
        </Typography>
        <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flex: 1,
                alignItems: "start",
              }}
            >
              <Autocomplete
                key={selectedProjectType?.id}
                startDecorator="Project Type:"
                options={projectTypes ?? []}
                value={selectedProjectType}
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                slotProps={{
                  listbox: { sx: { zIndex: 9999 } },
                }}
                onChange={(_, value) =>
                  setScale((v) => ({
                    ...v,
                    projectTypeID: value?.id ?? undefined,
                  }))
                }
              />
              <Autocomplete
                key={selectedTaskType?.id}
                startDecorator="Task Type:"
                options={taskTypes ?? []}
                value={selectedTaskType}
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                slotProps={{
                  listbox: { sx: { zIndex: 9999 } },
                }}
                onChange={(_, value) =>
                  setScale((v) => ({
                    ...v,
                    taskTypeID: value?.id ?? undefined,
                  }))
                }
              />
              <Input
                fullWidth
                startDecorator="Rate:"
                value={
                  rateFocused
                    ? rateValue
                    : currencyFormatter.format(Number(rateValue) ?? 0)
                }
                onFocus={() => setRateFocused(true)}
                onBlur={() => {
                  setRateFocused(false);
                }}
                onChange={(e) => setRateValue(e.target.value)}
              />
            </Box>
          </Box>
        )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {scaleID ? (
          <Button variant="soft" onClick={askDelete} color="danger">
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="neutral" variant="soft">
          Cancel
        </Button>
        <Button loading={isCreating || isUpdating} onClick={save}>
          {scaleID ? "Save" : "Create"}
        </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

function formatEIN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid EIN: must contain exactly 9 digits.");
    }

    // Format as XX-XXXXXXX
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } catch (error) {
    return "";
  }
}

function formatSSN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid SSN: must contain exactly 9 digits.");
    }

    // Format as XXX-XX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  } catch (error) {
    return "";
  }
}
